<template>
  <BasePage :pageTitle="pageTitle" back-button show-menu show-footer>

    <!-- Loading State -->
    <LoadingSpinner v-if="isLoading" :text="__DICT__.loading" container-class="mt-2"/>

    <!-- Error State -->
    <div v-else-if="!passenger" class="alert alert-important alert-danger mt-2" role="alert">
      {{ errorMsg || __DICT__.passengerLoadError }}
    </div>

    <!-- Passenger Profile -->
    <template v-if="passenger">
      <!-- Sticky Header -->
      <div class="passenger-header">
        <div class="passenger-info-card">
          <div style="min-width: 0; flex: 1;">
            <div class="d-flex align-items-center">
              <span class="passenger-ref">{{ passenger.reference }}</span>
              <button @click="copyBookingRef()" class="btn btn-icon btn-ghost-primary p-0 ms-1" style="width: 1.25rem; height: 1.25rem;">
                <IconCopy :size="13"/>
              </button>
            </div>
            <div v-if="passenger.phonenumber" class="passenger-phone">
              {{ passenger.phonenumber }}
            </div>
          </div>
          <button v-if="passenger.phonenumber" class="phone-btn"
                  @click="handlePhoneClick(passenger.phonenumber)">
            <IconPhone :size="20"/>
          </button>
        </div>

        <div v-if="passenger.status === 'cancelled'" class="mt-2">
          <span class="badge bg-danger text-white">{{ __DICT__.passengerView?.cancelled || 'CANCELLED' }}</span>
        </div>

        <!-- Tab Bar -->
        <TabBar class="mt-2" :tabs="tabs" v-model="activeTab"/>
      </div>

      <!-- Tab Content -->
      <div class="tab-content mt-3">
        <!-- Details Tab -->
        <div v-if="activeTab === 'details'">
          <Card show-status-bar margin="mb-3">
            <DetailField v-if="passenger.passengerName" :label="__DICT__.passengerView.name" :value="passenger.passengerName" />
            <DetailField v-if="passenger.reference" :label="__DICT__.passengerView.bookingRef" :value="passenger.reference" />
            <DetailField v-if="can('viewEmail') && passenger.email" :label="__DICT__.passengerView.email" :value="passenger.email" />
            <DetailField v-if="passenger.phonenumber" :label="__DICT__.passengerView.phone">
              <a class="link-azure" @click="handlePhoneClick(passenger.phonenumber)">{{ passenger.phonenumber }}</a>
            </DetailField>
            <DetailField v-if="can('viewSensitiveData') && passenger.next_of_kin_name" :label="__DICT__.passengerView.nok">
              {{ passenger.next_of_kin_name }}
              <a v-if="passenger.next_of_kin_phone" class="link-azure ms-1"
                 @click="handlePhoneClick(passenger.next_of_kin_phone)">{{ passenger.next_of_kin_phone }}</a>
            </DetailField>
            <DetailField v-if="can('viewSensitiveData') && passenger.dateOfBirth" :label="__DICT__.passengerView.dob" :value="formatDate(passenger.dateOfBirth)" />
            <DetailField v-if="passenger.dateOfBirth" :label="__DICT__.passengerView.age" :value="getAge(passenger.dateOfBirth)" />
            <DetailField v-if="passenger.nationality" :label="__DICT__.passengerView.nationality" :value="passenger.nationality" />
            <DetailField v-if="passenger.dietaryPreferences" :label="__DICT__.passengerView.dietaryPreferences" :value="passenger.dietaryPreferences" />
            <DetailField v-if="passenger.resortName" :label="__DICT__.passengerView.resort" :value="passenger.resortName" />
            <DetailField v-if="passenger.groupName" :label="__DICT__.passengerView.group" :value="passenger.groupName" />
          </Card>
        </div>

        <!-- Travel Tab -->
        <div v-if="activeTab === 'travel'">
          <TravelDetails :passenger="passenger"/>
        </div>

        <!-- Accommodation Tab -->
        <div v-if="activeTab === 'accommodation'">
          <AccommodationDetailsCard
              v-if="accommodationData"
              :accommodation="accommodationData"
              show-link
          />
          <div v-else class="text-center text-muted py-4">
            {{ __DICT__.passengerView.noAccommodationInfoAvailable }}
          </div>
        </div>

        <!-- Extras Tab: lift passes, lessons, equipment rental -->
        <div v-if="activeTab === 'extras'">
          <!-- Lift Pass -->
          <Card v-if="liftPassData" show-status-bar margin="mb-3">
            <template #header>
              <h4 class="card-title">{{ __DICT__.passengerView.liftPass }}</h4>
            </template>
            <DetailField v-if="liftPassData.passType" :label="__DICT__.passengerView.passType" :value="liftPassData.passType" />
            <DetailField v-if="liftPassData.duration" :label="__DICT__.passengerView.duration">
              {{ liftPassData.duration }} {{ __DICT__.passengerView?.daysSuffix || 'days' }}
            </DetailField>
            <DetailField v-if="liftPassData.firstDaysSkiing" :label="__DICT__.passengerView.firstDayOfSkiing" :value="formatDate(liftPassData.firstDaysSkiing)" />
            <DetailField v-if="liftPassData.distributionPoint" :label="__DICT__.passengerView.distributionPoint">
              {{ liftPassData.distributionPoint }}
              <a v-if="liftPassData.distributionLocation"
                 :href="googleMapsLink(liftPassData.distributionLocation)" target="_blank"
                 class="btn btn-icon btn-ghost-primary btn-sm ms-1">
                <IconMapPin :size="16"/>
              </a>
            </DetailField>
            <DetailField v-if="liftPassData.distributionStartTime || liftPassData.distributionEndTime"
                         :label="__DICT__.passengerView?.distributionWindow || 'Distribution Window'">
              <div v-if="liftPassData.distributionStartTime">
                <span class="small text-muted">{{ __DICT__.passengerView?.start || 'Start:' }}</span>
                {{ liftPassData.distributionStartTime }}
              </div>
              <div v-if="liftPassData.distributionEndTime">
                <span class="small text-muted">{{ __DICT__.passengerView?.end || 'End:' }}</span>
                {{ liftPassData.distributionEndTime }}
              </div>
            </DetailField>
          </Card>

          <!-- Lessons -->
          <LessonDetails
              v-for="lesson in lessonsData"
              :key="lesson.id"
              :lesson="lesson"
              title="Lesson"
              show-link
          />

          <!-- Equipment Rental -->
          <Card v-if="skiHireData" show-status-bar margin="mb-3">
            <template #header>
              <h4 class="card-title">{{ __DICT__.passengerView.skiHire }}</h4>
            </template>
            <DetailField v-if="skiHireData.package_name" :label="__DICT__.passengerView.package" :value="skiHireData.package_name" />
            <DetailField v-if="skiHireData.helmet" :label="__DICT__.passengerView.helmet" :value="skiHireData.helmet" />
            <DetailField v-if="skiHireData.rentalShop" :label="__DICT__.passengerView.shopName">
              {{ skiHireData.rentalShop?.name }}
              <a v-if="skiHireData.rentalShop?.locationUrl" :href="skiHireData.rentalShop.locationUrl"
                 target="_blank" class="btn btn-icon btn-ghost-primary btn-sm ms-1">
                <IconMapPin :size="16"/>
              </a>
            </DetailField>
            <DetailField v-if="skiHireData.rentalShop?.openingHours"
                         :label="__DICT__.passengerView?.openingHours || 'Opening Hours'"
                         :value="skiHireData.rentalShop.openingHours" />
          </Card>
          <div v-if="!liftPassData && !lessonsData.length && !skiHireData" class="text-center text-muted py-4">
            {{ __DICT__.passengerView?.noExtrasInfo || 'No extras information' }}
          </div>
        </div>

        <!-- Events Tab -->
        <div v-if="activeTab === 'events'">
          <EventDetailsCard v-for="event in eventData" :key="event.id" :event="event" show-link title="Event" />
          <div v-if="!eventData.length" class="text-center text-muted py-4">
            {{ __DICT__.passengerView?.noEventsInfo || 'No events information' }}
          </div>
        </div>
      </div>
    </template>

    <PhoneNumberActionSheet
        :phone-number="selectedPhoneNumber"
        :is-visible="showActionSheet"
        @close="showActionSheet = false"
    />
  </BasePage>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue'
import {useRoute} from 'vue-router'
import {useWeekStore} from '@/stores/week.js'
import BasePage from "@/components/BasePage.vue"
import {LoadingSpinner, Card, TabBar, DetailField} from "@/components/atomic";
import {IconMapPin, IconCopy, IconPhone} from "@tabler/icons-vue"
import AccommodationDetailsCard from "@/components/AccommodationDetailsCard.vue";
import LessonDetails from "@/components/LessonDetails.vue";
import EventDetailsCard from "@/components/EventDetailsCard.vue";
import PhoneNumberActionSheet from "@/components/PhoneNumberActionSheet.vue";
import TravelDetails from "@/components/TravelDetails.vue";
import {usePermissions} from '@/composables/usePermissions'
import {passengerIndex} from "@/services/PassengerIndex.js";

const {can} = usePermissions()
const route = useRoute()
const weekStore = useWeekStore()
const passenger = ref(null)
const isLoading = ref(true)
const showActionSheet = ref(false)
const selectedPhoneNumber = ref('')
const errorMsg = ref(null)
const activeTab = ref('details')

const tabs = [
  {key: 'details', label: __DICT__.passengerView?.tabs?.details || 'Details'},
  {key: 'travel', label: __DICT__.passengerView?.tabs?.travel || 'Travel'},
  {key: 'accommodation', label: __DICT__.passengerView?.tabs?.accommodation || 'Accomm.'},
  {key: 'extras', label: __DICT__.passengerView?.tabs?.extras || 'Extras'},
  {key: 'events', label: __DICT__.passengerView?.tabs?.events || 'Events'},
]

const pageTitle = computed(() =>
    passenger.value ? `${passenger.value.passengerName}` : `${__DICT__.passenger} ${__DICT__.details}`
)

const handlePhoneClick = (number) => {
  selectedPhoneNumber.value = number
  showActionSheet.value = true
}

onMounted(async () => {
  const bookingRef = route.params.bookingRef
  if (can('canViewPassenger', {ref: bookingRef})) {
    try {
      await weekStore.weekData
      passenger.value = passengerIndex.getByReference(bookingRef)
    } catch (error) {
      console.error('Error loading passenger data:', error)
    } finally {
      isLoading.value = false
    }
  } else {
    errorMsg.value = __DICT__.passengerView?.noPermission || "You do not have permissions to view this page"
    isLoading.value = false
  }
})

function formatDate(dateString) {
  if (!dateString) return __DICT__.notAvailable || 'N/A'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'})
}

function getAge(dateOfBirth) {
  if (!dateOfBirth) return null
  const dob = new Date(dateOfBirth)
  const today = new Date()
  if (isNaN(dob.getTime())) return null
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--
  }
  return age
}

const copyBookingRef = async () => {
  try {
    await navigator.clipboard.writeText(passenger.value.reference);
  } catch (error) {
    console.error('Failed to copy text to clipboard:', error);
  }
}

const liftPassData = computed(() => {
  if (!passenger.value?.allocPlaces?.length) return null
  return passenger.value.allocPlaces.find(place => place.liftpass)?.liftpass
})

const skiHireData = computed(() => {
  if (!passenger.value?.allocPlaces?.length) return null
  return passenger.value.allocPlaces.find(place => place.skihire)?.skihire
})

const lessonsData = computed(() => {
  if (!passenger.value?.allocPlaces?.length) return []
  return passenger.value.allocPlaces
      .filter(place => place.lessons)
      .map(place => place.lessons)
})

const eventData = computed(() => {
  if (!passenger.value?.allocPlaces?.length) return []
  return passenger.value.allocPlaces
      .filter(place => place.event)
      .map(place => place.event)
})

const accommodationData = computed(() => {
  if (!passenger.value?.allocPlaces?.length) return null
  return passenger.value.allocPlaces.find(place => place.accommodation)?.accommodation
})

const googleMapsLink = (loc) => {
  try {
    return `https://www.google.com/maps?q=${loc.lat},${loc.lng}`;
  } catch (e) {
    console.error(e)
  }
}
</script>

<style scoped>
.passenger-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: var(--tblr-bg-surface);
  padding: 0.5rem 0 0;
  margin: -0.5rem 0 0 0;
}

.passenger-info-card {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 12px;
  border: 1px solid var(--tblr-border-color);
  background-color: var(--tblr-bg-surface);
}

.passenger-ref {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--tblr-body-color);
}

.passenger-phone {
  font-size: 0.875rem;
  color: rgba(var(--tblr-body-color-rgb), 0.6);
  margin-top: 0.0625rem;
}

.phone-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  border: none;
  flex-shrink: 0;
  background-color: var(--tblr-primary);
  color: #fff;
  cursor: pointer;
  transition: opacity 0.15s ease;
}

.phone-btn:hover {
  opacity: 0.85;
}
</style>
